import type { ThreadMessage } from '@assistant-ui/react';

// Collect the providers an assistant turn flagged as needing connection
// (`connect_required` data parts), in order and de-duplicated. The backend emits
// one part per missing provider, so a single turn can carry several.
export function requiredProviders(message: ThreadMessage | undefined): string[] {
  if (!message || message.role !== 'assistant') return [];
  const seen = new Set<string>();
  const providers: string[] = [];
  for (const part of message.content) {
    if (part.type === 'data' && part.name === 'connect_required') {
      const provider = (part.data as { provider?: string }).provider;
      if (provider && !seen.has(provider)) {
        seen.add(provider);
        providers.push(provider);
      }
    }
  }
  return providers;
}

// The backend signal is authoritative: it comes from the runtime's live MCP
// check, which catches a stored token the upstream rejects — a case the
// connection-status endpoint still reports as "connected". So the only reason to
// hide a prompt is that the user connected that provider after the message was
// produced; a later turn that still can't reach it prompts again.
export function pendingProviders(
  messageCreatedAt: Date | undefined,
  providers: string[],
  connectedAt: Readonly<Record<string, number>>,
): string[] {
  const askedAt = messageCreatedAt?.getTime();
  return providers.filter((provider) => {
    const connected = connectedAt[provider];
    return askedAt === undefined || connected === undefined || connected < askedAt;
  });
}

const CONNECTED_AT_KEY_PREFIX = 'agenthub:provider-connected-at:';

// Persisted so a reload (or a new chat's post-first-turn remount) doesn't
// re-prompt for a provider the user just connected. Keyed per user so one
// person's connect can't hide another's prompt in a shared browser; without a
// known user nothing is persisted.
export function readConnectedAt(userId: string | undefined): Record<string, number> {
  if (!userId) return {};
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(CONNECTED_AT_KEY_PREFIX + userId) ?? '{}');
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, number>) : {};
  } catch {
    return {};
  }
}

export function recordConnected(
  userId: string | undefined,
  provider: string,
  at: number = Date.now(),
): Record<string, number> {
  const next = { ...readConnectedAt(userId), [provider]: at };
  if (!userId) return next;
  try {
    window.localStorage.setItem(CONNECTED_AT_KEY_PREFIX + userId, JSON.stringify(next));
  } catch {
    // Storage unavailable: the in-memory value still hides the prompt this session.
  }
  return next;
}
